/**
 * AttachmentArea — renders attachment chips above the chat input.
 *
 * File processing logic (paste, drop, attach) is handled by the
 * exported `processFiles` and `processDriveItems` functions,
 * called from ChatPanel with callbacks that update state.
 */
import { EyeOff } from 'lucide-react'
import { AttachmentChip } from './AttachmentChip.js'
import { routeFile, createAttachmentId, readFileAsDataUrl, isEncryptedPdf, overflowRoute, aiVisibilityNotice, aiNoticeMessage, messageTextBudgetNotices, isModelTextMime } from './attachmentLogic.js'
import { useTranslation } from '@papan/i18n'
import { uploadSessionAttachment } from './sessionAttachmentsApi.js'
import { filesApi } from '@papan/files/client'
import type { PapanDragPayload } from '@papan/core-ui/ui/papanDrag'
import type { Attachment } from './hooks.js'

interface AttachmentAreaProps {
  attachments: Attachment[]
  onRemove: (id: string) => void
  onOpen?: (path: string) => void
  disabled?: boolean
  /** Session + workspace context (#639 Task 5.2), threaded to each chip so a
   *  handle-bearing (already-uploaded, not-yet-sent) attachment can open the
   *  live-gated viewer the same way a sent one does. */
  sessionId?: string | null
  workspaceId?: string
  /** Fires after a composer chip's "Move to drive…" succeeds (#662 Task 8).
   *  Composer chips never carry a `messageId` (nothing is persisted yet), so
   *  `AttachmentChip`'s own re-point call is a local no-op here — the caller
   *  must patch the matching entry in its `attachments` state itself, via
   *  `markAttachmentMoved` (`attachmentLogic.ts`), or the moved attachment
   *  stays inline/handle-bound in the message that eventually gets sent. */
  onMoved?: (partId: string, drive: { fileId: string; path: string }) => void
}

export function AttachmentArea({ attachments, onRemove, onOpen, disabled, sessionId, workspaceId, onMoved }: AttachmentAreaProps) {
  const { t } = useTranslation('agent')
  if (attachments.length === 0) return null
  // #1292: a file that will be attached but NOT seen by the AI says so here, in
  // the composer, before the user sends — otherwise they believe the AI looked.
  // The per-message text budget's notice depends on the chips before it, so it is
  // derived from the whole list here rather than stored on the chip.
  const overTotal = messageTextBudgetNotices(attachments)
  const shown = attachments.map(a => (overTotal.has(a.id) ? { ...a, aiNotice: overTotal.get(a.id) } : a))
  const notices = shown.filter(a => a.aiNotice && a.status !== 'error')

  return (
    <div className="px-1 pt-1">
      <div className="flex flex-wrap gap-1">
        {shown.map(att => (
          <AttachmentChip
            key={att.id}
            attachment={att}
            onRemove={disabled ? undefined : () => onRemove(att.id)}
            onOpen={onOpen}
            sessionId={sessionId}
            workspaceId={workspaceId}
            onMoved={onMoved}
          />
        ))}
      </div>
      {notices.length > 0 && (
        <ul className="mt-1 space-y-0.5" data-testid="attachment-ai-notices">
          {notices.map(att => (
            <li key={att.id} role="status" className="flex items-start gap-1 text-[11px] leading-snug text-amber-500">
              <EyeOff size={11} className="mt-[2px] shrink-0" aria-hidden />
              <span><span className="font-medium">{att.filename}:</span> {aiNoticeMessage(att.aiNotice!, t)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Process files from various sources and add as attachments.
 *
 * Binary bytes NEVER land in the project drive from here (#639, Task 3.4):
 * `routeFile` decides per file —
 *   - 'inline': bytes stay in the message part only (data URL), no upload
 *     at all.
 *   - 'upload': bytes go to the session-private CAS store
 *     (`uploadSessionAttachment`), never `.temp/` on the drive. The
 *     resulting `handle` rides the attachment through to the outgoing
 *     message part.
 *   - 'blocked': a view-only caller tried to create new bytes from
 *     paste/drop/local/attach — refused with a reason, no upload, no
 *     inline.
 *   - 'insert-path': not reached here (this function never sees
 *     `source: 'drive'`; see `processDriveItems`).
 */
export async function processFiles(
  files: File[],
  source: 'paste' | 'drop' | 'attach',
  callbacks: {
    onAdd: (attachment: Attachment) => void
    onUpdateProgress: (id: string, progress: number) => void
    onReady: (id: string, updates: Partial<Attachment>) => void
    onError: (id: string) => void
    /** A file was refused by `routeFile` (view-only, or any future block
     *  reason) — never uploaded, never inlined. */
    onBlocked?: (filename: string, reason: string) => void
  },
  /** Session-store destination + write permission. `canWrite` gates
   *  paste/drop/local/attach uploads (invariant #3) — never hardcode
   *  `true`; thread the caller's real workspace-write status.
   *
   *  `reserveInline` (#1292) is the composer's per-MESSAGE inline budget
   *  (`inlineBudget.ts`): asked synchronously, right after routing and before
   *  the chip is added, so concurrent paste/drop/pick can't double-book it.
   *  `false` routes the file to the upload instead. Absent = no message budget
   *  (only the per-file limit), the pre-#1292 behaviour.
   *
   *  `uploadSignal` (#1292) cancels an upload whose chip was removed. */
  ctx: {
    sessionId: string
    workspaceId: string
    canWrite: boolean
    reserveInline?: (id: string, bytes: number) => boolean
    uploadSignal?: (id: string) => AbortSignal | undefined
  },
): Promise<void> {
  for (const file of files) {
    const id = createAttachmentId()
    let route = routeFile({
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size,
      source,
      canWrite: ctx.canWrite,
    })
    // #1292: the route is decided — and the budget reserved — here, once.
    if (route.action === 'inline' && ctx.reserveInline && !ctx.reserveInline(id, file.size)) {
      route = overflowRoute(source, route.mime)
    }

    if (route.action === 'insert-path') {
      continue
    }

    if (route.action === 'blocked') {
      callbacks.onBlocked?.(file.name, route.reason)
      continue
    }

    const mime = route.mime
    const aiNotice = aiVisibilityNotice(mime, file.size)
    const att: Attachment = {
      id,
      filename: file.name,
      mime,
      source,
      status: 'uploading',
      progress: route.action === 'upload' ? 0 : undefined,
      ...(route.action === 'inline' ? { inlineBytes: file.size } : {}),
      ...(isModelTextMime(mime) ? { textBytes: file.size } : {}),
      ...(aiNotice ? { aiNotice } : {}),
    }
    callbacks.onAdd(att)

    try {
      if (route.action === 'inline') {
        // Inline-only: bytes stay in the message part (data URL). No
        // parallel drive/session-store upload — this is the fix for the
        // "always upload so agent has a file path" leak.
        let url: string | undefined
        if (mime === 'application/pdf' && await isEncryptedPdf(file)) {
          // No inline url — providers reject encrypted-PDF bytes as
          // invalid_file. The attachment reaches the AI with no bytes and
          // no path (it was never uploaded), so unresolved for now — same
          // trade-off as before, minus the drive copy.
        } else {
          url = await readFileAsDataUrl(file)
        }
        callbacks.onReady(id, {
          url,
          filename: file.name,
          status: 'ready',
          // An encrypted PDF carries no bytes, so it holds none of the budget.
          ...(url ? {} : { inlineBytes: 0 }),
        })
      } else {
        // 'upload' — session-private CAS store, never the drive/.temp.
        // The server's `source` only knows 'upload' | 'paste' | 'drop'
        // (attachments.ts ALLOWED_SOURCES) — the composer's local-attach
        // source ('attach', the paperclip button) maps to 'upload'.
        const uploadSource = source === 'attach' ? 'upload' : source
        const result = await uploadSessionAttachment(ctx.sessionId, ctx.workspaceId, file, {
          idempotencyKey: id,
          source: uploadSource,
          signal: ctx.uploadSignal?.(id),
        })
        callbacks.onReady(id, {
          handle: result.handle,
          filename: file.name,
          status: 'ready',
        })
      }
    } catch {
      callbacks.onError(id)
    }
  }
}

/**
 * Process Papan Drive items dropped onto the chat input.
 *
 * Every non-folder item is routed through the same `routeFile` gate
 * `processFiles` uses, with `source: 'drive'`. The outcome is decided by the
 * caller's write permission (see `attachmentLogic.ts`):
 *   - WRITER + inlinable image/PDF → `inline`: a chip is shown AND the path is
 *     inserted. Bytes are fetched from the drive and base64'd into the message
 *     part so the model sees the file in-turn — a deliberate copy of a file the
 *     writer already controls (restores the pre-#639 behavior).
 *   - view-only actor, or a non-inlinable / too-large file → `insert-path`:
 *     reference the original by path, never re-embed its bytes. This preserves
 *     the #639 fix — a view-only user must never mint new bytes from a
 *     read-only file. For an image/PDF a reference CHIP (a pointer, no bytes)
 *     is still shown so both roles look the same; the server renders that
 *     path-only media part as a "read it with tools" pointer.
 */
/** Image/PDF MIME types that earn a reference CHIP on a path-only drive drop
 *  (a view-only actor, or an over-size file). Mirrors the inlinable-media set
 *  the writer path uses — everything else stays a bare path in the composer. */
function isReferenceableMime(mime: string): boolean {
  return mime.startsWith('image/') || mime === 'application/pdf'
}

export async function processDriveItems(
  transferData: PapanDragPayload,
  callbacks: {
    onAdd: (attachment: Attachment) => void
    onReady: (id: string, updates: Partial<Attachment>) => void
    onError: (id: string) => void
    onInsertPath?: (path: string) => void
    /** A drive item was refused by `routeFile` (reserved — a view-only drive
     *  drag resolves to `insert-path`, not `blocked`, so drive items never
     *  block today; kept for parity with `processFiles` and so a future
     *  routeFile rule change is covered without another call-site edit). */
    onBlocked?: (filename: string, reason: string) => void
  },
  /** Whether the caller can write to the workspace. A WRITER inlines an
   *  inlinable drive image/PDF (chip + bytes) for in-turn vision; a view-only
   *  actor only ever references by path. Never hardcode `true` — thread the
   *  caller's real workspace-write status (mirrors `processFiles`). */
  ctx: { canWrite: boolean; reserveInline?: (id: string, bytes: number) => boolean },
): Promise<void> {
  for (const item of transferData.files) {
    if (item.isFolder) {
      // Look up the full path — `item.name` alone misses parent
      // segments (e.g. dragging `/papan/git/papan` would otherwise
      // render as `/papan/papan`). getFileInfo works for folders too
      // and returns the computed virtual path.
      try {
        const info = await filesApi.getFileInfo(item.id)
        callbacks.onInsertPath?.(info.path || `/papan/${item.name}`)
      } catch {
        callbacks.onInsertPath?.(`/papan/${item.name}`)
      }
      continue
    }

    try {
      const info = await filesApi.getFileInfo(item.id)
      const ext = item.name.split('.').pop()?.toLowerCase() || ''
      const VISION_EXT_MIMES: Record<string, string> = {
        png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
        gif: 'image/gif', webp: 'image/webp', pdf: 'application/pdf',
      }
      const storedMime = info.mimeType && info.mimeType !== 'application/octet-stream' ? info.mimeType : null
      const mime = storedMime || VISION_EXT_MIMES[ext] || 'application/octet-stream'
      const filePath = info.path || `/papan/${item.name}`

      const inlineId = createAttachmentId()
      let route = routeFile({
        name: item.name,
        type: mime,
        size: info.size,
        source: 'drive',
        canWrite: ctx.canWrite,
      })
      // #1292: over the message's inline budget → referenced by path instead.
      if (route.action === 'inline' && ctx.reserveInline && !ctx.reserveInline(inlineId, info.size)) {
        route = overflowRoute('drive', route.mime)
      }

      if (route.action === 'blocked') {
        callbacks.onBlocked?.(item.name, route.reason)
        continue
      }

      if (route.action === 'inline') {
        // Writer + inlinable image/PDF: show a chip AND insert the path. The
        // bytes give the model in-turn vision; the path lets it edit the file
        // in place. Fetched from the drive and base64'd into the message part
        // (owner-gated) — a deliberate copy of a file the writer controls.
        const id = inlineId
        callbacks.onAdd({ id, filename: item.name, mime: route.mime, source: 'drive', status: 'uploading', inlineBytes: info.size })
        callbacks.onInsertPath?.(filePath)
        try {
          const blob = await filesApi.getFileContent(item.id)
          const file = new File([blob], item.name, { type: route.mime })
          // Encrypted PDFs are rejected by providers as invalid_file — don't
          // inline the bytes. Unlike processFiles we still have the drive path,
          // so the chip keeps it: the agent decrypts the original in place
          // (cred_to_env + pymupdf) instead of failing the turn.
          if (route.mime === 'application/pdf' && await isEncryptedPdf(file)) {
            callbacks.onReady(id, { path: filePath, status: 'ready' })
          } else {
            const url = await readFileAsDataUrl(file)
            callbacks.onReady(id, { url, path: filePath, status: 'ready' })
          }
        } catch {
          // The drive fetch/read failed — but we already have the drive `path`
          // (inserted above and held here). Degrade to a path-reference chip
          // (the same shape the encrypted-PDF and view-only branches use)
          // rather than `onError`, which would leave a chip with no url/path/
          // handle — permanently unopenable. The agent can still read the
          // original in place via read_file.
          callbacks.onReady(id, { path: filePath, status: 'ready' })
        }
        continue
      }

      // 'insert-path' — view-only actor, or a non-inlinable / too-large file:
      // reference the original by path, never re-embed its bytes.
      callbacks.onInsertPath?.(filePath)
      // For an image/PDF, ALSO surface a reference CHIP (a pure pointer, no
      // bytes) so a view-only drag — or a writer's over-size file — looks the
      // same as a writer's inline chip. The agent reads the original in place
      // via read_file; the server renders a path-only media part as a "read it
      // with tools" pointer (see convert-messages.ts), never inline bytes.
      if (isReferenceableMime(route.mime)) {
        callbacks.onAdd({
          id: createAttachmentId(),
          filename: item.name,
          mime: route.mime,
          source: 'drive',
          status: 'ready',
          path: filePath,
        })
      }
    } catch {
      callbacks.onInsertPath?.(`/papan/${item.name}`)
    }
  }
}
